// Files tab — three groups under one visit: Uploads (FileBrowser), Transcripts
// (meetings/*/transcript.md, read-only), Reports (…/reports/*.html|pdf).
import { useEffect, useState } from "react";
import { Card, EmptyState, Button, Pill } from "../../design/ui";
import { listFiles, presignFile, type StoreObject } from "../../lib/brain-api";
import { FileBrowser } from "../FileBrowser";

function fileName(key: string): string {
  return key.split("/").pop() ?? key;
}

function fmtWhen(iso: string | undefined): string {
  if (!iso) return "";
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
}

function FileRows({ files, empty, tagFor }: {
  files: StoreObject[] | null;
  empty: string;
  tagFor?: (key: string) => string | null;
}) {
  const open = async (key: string) => {
    try { window.open(await presignFile(key), "_blank", "noopener"); } catch { /* best-effort */ }
  };

  if (files === null) {
    return <p style={{ fontSize: 13, color: "var(--kt-text-muted)", margin: 0 }}>Loading…</p>;
  }
  if (files.length === 0) {
    return <p style={{ fontSize: 13, color: "var(--kt-text-muted)", margin: 0 }}>{empty}</p>;
  }

  return (
    <div style={{ display: "flex", flexDirection: "column" }}>
      {files.map((f) => {
        const tag = tagFor?.(f.key);
        return (
          <button
            key={f.key}
            type="button"
            className="kt-menu-item"
            style={{
              display: "flex", alignItems: "center", gap: 10, width: "100%",
              padding: "8px 6px", borderRadius: 8, textAlign: "left",
            }}
            onClick={() => void open(f.key)}
          >
            <span style={{ flex: 1, minWidth: 0, fontSize: 13, color: "var(--kt-ink)", overflow: "hidden", textOverflow: "ellipsis" }}>
              {fileName(f.key)}
            </span>
            {tag && <Pill>{tag}</Pill>}
            <span style={{ fontSize: 11, fontFamily: "var(--font-mono)", color: "var(--kt-text-muted)", flexShrink: 0 }}>
              {fmtWhen(f.lastModified)}
            </span>
          </button>
        );
      })}
    </div>
  );
}

export function FilesTab({ customerId, visitId, reportsVersion }: {
  customerId: string;
  visitId: string;
  reportsVersion: number;
}) {
  const [transcripts, setTranscripts] = useState<StoreObject[] | null>(null);
  const [reports, setReports] = useState<StoreObject[] | null>(null);

  useEffect(() => {
    let cancelled = false;
    listFiles(`customers/${customerId}/visits/${visitId}/meetings/`)
      .then((objs) => {
        if (cancelled) return;
        setTranscripts(
          objs
            .filter((o) => o.key.endsWith("/transcript.md") || o.key.endsWith("transcript.md"))
            .sort((a, b) => (b.lastModified ?? "").localeCompare(a.lastModified ?? "")),
        );
      })
      .catch(() => { if (!cancelled) setTranscripts([]); });
    return () => { cancelled = true; };
  }, [customerId, visitId]);

  useEffect(() => {
    let cancelled = false;
    listFiles(`customers/${customerId}/visits/${visitId}/reports/`)
      .then((objs) => {
        if (cancelled) return;
        setReports(
          [...objs].sort((a, b) => (b.lastModified ?? "").localeCompare(a.lastModified ?? "")),
        );
      })
      .catch(() => { if (!cancelled) setReports([]); });
    return () => { cancelled = true; };
  }, [customerId, visitId, reportsVersion]);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <Card>
        <div className="kt-record-label" style={{ marginBottom: 10 }}>Uploads</div>
        <FileBrowser
          prefix={`customers/${customerId}/visits/${visitId}/files/`}
          tags={{ customer: customerId, source: "visit", tier: "company" }}
        />
      </Card>

      <Card>
        <div className="kt-record-label" style={{ marginBottom: 10 }}>Transcripts</div>
        <FileRows
          files={transcripts}
          empty="No transcripts yet — add one from the Overview tab."
          tagFor={() => "read-only"}
        />
      </Card>

      <Card>
        <div className="kt-record-label" style={{ marginBottom: 10 }}>Reports</div>
        {reports !== null && reports.length === 0 ? (
          <EmptyState icon="file-text" title="No reports exported yet">
            Use <strong>Export report</strong> in the header to freeze HTML + PDF.
          </EmptyState>
        ) : (
          <FileRows
            files={reports}
            empty="No reports exported yet."
            tagFor={(key) => {
              if (key.endsWith(".pdf")) return "PDF";
              if (key.endsWith(".html")) return "HTML";
              return null;
            }}
          />
        )}
        {reports !== null && reports.length > 0 && (
          <div style={{ marginTop: 8 }}>
            <Button size="sm" icon="refresh" onClick={() => {
              setReports(null);
              listFiles(`customers/${customerId}/visits/${visitId}/reports/`)
                .then((objs) => setReports([...objs].sort((a, b) => (b.lastModified ?? "").localeCompare(a.lastModified ?? ""))))
                .catch(() => setReports([]));
            }}>
              Refresh
            </Button>
          </div>
        )}
      </Card>
    </div>
  );
}
